import { type ComponentType, type ReactNode } from 'react';
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  Info,
  type LucideProps,
} from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Callout — surface attention-grabbing messages (warnings, blockers, tips)
 * with consistent tone. Use sparingly; not every notice needs a callout.
 *
 *   <Callout tone="warning" title="Reports are missing traffic data"
 *            action={<Button>Connect Google</Button>}>
 *     GA4/GSC not connected. Reports will run but skip ranking sections.
 *   </Callout>
 */
export type CalloutTone = 'info' | 'success' | 'warning' | 'danger';

const TONE: Record<
  CalloutTone,
  { wrap: string; iconWrap: string; icon: ComponentType<LucideProps> }
> = {
  info: {
    wrap: 'border-info/30 bg-info/5',
    iconWrap: 'bg-info/15 text-info',
    icon: Info,
  },
  success: {
    wrap: 'border-success/30 bg-success/5',
    iconWrap: 'bg-success/15 text-success',
    icon: CheckCircle2,
  },
  warning: {
    wrap: 'border-warning/30 bg-warning/5',
    iconWrap: 'bg-warning/15 text-warning',
    icon: AlertTriangle,
  },
  danger: {
    wrap: 'border-destructive/30 bg-destructive/5',
    iconWrap: 'bg-destructive/15 text-destructive',
    icon: AlertOctagon,
  },
};

export interface CalloutProps {
  tone?: CalloutTone;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  /** Override the default icon for this tone. */
  icon?: ComponentType<LucideProps>;
  className?: string;
}

export function Callout({
  tone = 'info',
  title,
  children,
  action,
  icon,
  className,
}: CalloutProps): JSX.Element {
  const t = TONE[tone];
  const Icon = icon ?? t.icon;
  return (
    <div
      role="status"
      className={cn(
        'flex items-start gap-3 rounded-xl border p-3.5 text-sm',
        t.wrap,
        className,
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
          t.iconWrap,
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        {title ? (
          <div className="text-sm font-semibold text-foreground">{title}</div>
        ) : null}
        {children ? (
          <div className="text-sm text-muted-foreground">{children}</div>
        ) : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}
